'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { NuqsAdapter } from 'nuqs/adapters/next/app';
import { createProviders } from '@/helpers/providers';
import { TooltipProvider } from '@/ui/tooltip';
import { QueryProvider } from './query-provider';
import { BusProvider } from './bus-provider';
import { DeviceProvider } from './device-provider';
import { DebugProvider } from './debug-provider';
import { ThemeProvider } from './theme-provider';
import { HeadlessGuard } from './headless-guard';
import { AuthProvider } from './auth-provider';
import { ConfirmProvider } from './confirm-provider';
import { Toaster } from '@/ui/sonner';
import { BreakpointIndicator } from '@/components/debug/breakpoint-indicator';

// 'use client' alone doesn't skip SSR — a client component still pre-renders
// on the server and hydrates, which is where hydration errors come from.
// Gating on mount forces this whole subtree (event bus, window-dependent
// providers, etc.) to only ever run in the browser.
const ClientComponent = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  const [mounted, setMounted] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setMounted(true);
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  if (!mounted) {
    return null;
  }
  return children;
};
const AppProviders = createProviders([[QueryProvider], [NuqsAdapter], [BusProvider], [DeviceProvider], [DebugProvider], [ThemeProvider], [HeadlessGuard], [AuthProvider], [TooltipProvider], [ConfirmProvider]]);

// Toaster and BreakpointIndicator aren't context providers (they don't wrap
// children, they just render their own fixed-position portal/overlay), so
// they sit as siblings here instead of joining the AppProviders array — same
// mount-gated subtree as everything else, since both read settings via
// useSettings.
export const Providers = t0 => {
  const $ = _c(6);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <AppProviders>{children}</AppProviders>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Toaster />;
    t3 = <BreakpointIndicator />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1) {
    t4 = <ClientComponent>{t1}{t2}{t3}</ClientComponent>;
    $[4] = t1;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};